import { usePostOfficeContext } from '@atlassian/post-office-context';
import type { CompletionStatus, OptionsV1 } from '@atlassiansox/cross-flow-api-internals';
import { Journeys, Targets, useCrossFlow } from '@atlassiansox/cross-flow-support';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseLoomAiMeetingNotesCrossFlow {
	openCrossFlow: () => void;
	isLoading: boolean;
}

interface UseLoomAiMeetingNotesProps {
	pollForProvisioningSuccess: () => Promise<void>;
}

const options: OptionsV1 = {
	journey: Journeys.GET_STARTED,
	sourceComponent: 'confluenceLoomSideNavBanner',
	sourceContext: 'confluenceLoomSideNavBannerLearnMore',
	targetProduct: Targets.LOOM,
	v1: true,
};

/**
 * Custom hook for managing Loom AI meeting notes cross-flow functionality.
 *
 * This hook provides functionality to open a cross-flow to Loom for AI meeting notes
 * with loading state management and prevents duplicate requests.
 *
 * @returns An object containing the openCrossFlow function and isLoading state
 *
 * @example
 * ```tsx
 * // Using in a banner component
 * const LoomBanner = () => {
 *   const { openCrossFlow, isLoading } = useLoomAiMeetingNotesCrossFlow({
 * 		pollForProvisioningSuccess: callback
 *   });
 *
 *   const handleLearnMore = () => {
 *     openCrossFlow();
 *   };
 *
 *   return (
 *     <div className="banner">
 *       <p>Enhance your meetings with AI-powered notes</p>
 *       <button onClick={handleLearnMore} disabled={isLoading}>
 *         Learn More
 *       </button>
 *     </div>
 *   );
 * };
 * ```
 */
export const useLoomAiMeetingNotesCrossFlow = ({
	pollForProvisioningSuccess,
}: UseLoomAiMeetingNotesProps): UseLoomAiMeetingNotesCrossFlow => {
	const crossFlowPromise = useRef<Promise<CompletionStatus> | null>(null);
	const unmountedRef = useRef(false);
	const { isSiteAdmin } = usePostOfficeContext();

	const [isLoading, setIsLoading] = useState(false);

	const crossFlow = useCrossFlow();

	const openCrossFlow = useCallback(() => {
		if (!crossFlow.isEnabled) {
			return;
		}

		if (crossFlowPromise.current === null) {
			setIsLoading(true);
			crossFlowPromise.current = crossFlow.apiV1
				.open(options)
				.then(async (result) => {
					if (result.success && isSiteAdmin) {
						await pollForProvisioningSuccess();
					}
					return result;
				})
				.finally(() => {
					crossFlowPromise.current = null;
					if (!unmountedRef.current) {
						/**
						 * Avoid state updates after unmount
						 */
						setIsLoading(false);
					}
				});
		}
	}, [crossFlow, pollForProvisioningSuccess, isSiteAdmin]);

	/**
	 * Clean up state on unmount
	 */
	useEffect(
		() => () => {
			unmountedRef.current = true;
		},
		[],
	);

	return {
		openCrossFlow,
		isLoading,
	};
};
